import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Palette, Pencil, Save, Shield, Trash2, Upload, UserCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/auth';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { useChatSettings } from '../hooks/useChatSettings';

// ---------------------------------------------------------------------------
// Types & defaults
// ---------------------------------------------------------------------------

interface ProfileSettings {
  displayName: string;
  bio: string;
  bannerColor: string;
  avatarColor: string;
  avatarImageId: string | null;
  avatarPreviewUrl: string | null;
}

interface NotificationSettings {
  newMessages: 'all' | 'dms' | 'never';
  reactions: boolean;
  channelAdded: boolean;
  soundDMs: boolean;
  soundChannels: boolean;
}

interface AppearanceSettings {
  showEmojis: boolean;
  showReactions: boolean;
  compactMode: boolean;
  accentColor: string;
  channelListView: 'sections' | 'unified';
  backgroundImageId: string | null;
  backgroundPreviewUrl: string | null;
  backgroundBlur: boolean;
  backgroundColor: string;
}

interface PrivacySettings {
  whoCanDM: 'everyone' | 'nobody';
  readReceipts: boolean;
  showOnlineStatus: boolean;
}

interface ChatSettings {
  profile: ProfileSettings;
  notifications: NotificationSettings;
  appearance: AppearanceSettings;
  privacy: PrivacySettings;
}

const DEFAULT_SETTINGS: ChatSettings = {
  profile: {
    displayName: '',
    bio: '',
    bannerColor: '#cbbfb5',
    avatarColor: '#5865F2',
    avatarImageId: null,
    avatarPreviewUrl: null,
  },
  notifications: {
    newMessages: 'all',
    reactions: true,
    channelAdded: true,
    soundDMs: true,
    soundChannels: false,
  },
  appearance: {
    showEmojis: true,
    showReactions: true,
    compactMode: false,
    accentColor: '#5865F2',
    channelListView: 'sections' as const,
    backgroundImageId: null,
    backgroundPreviewUrl: null,
    backgroundBlur: false,
    backgroundColor: '',
  },
  privacy: {
    whoCanDM: 'everyone',
    readReceipts: true,
    showOnlineStatus: true,
  },
};

// ---------------------------------------------------------------------------
// Color utilities (HSV ↔ HEX)
// ---------------------------------------------------------------------------

function hexToHsv(hex: string): { h: number; s: number; v: number } {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;

  let h = 0;
  if (diff !== 0) {
    if (max === r) h = ((g - b) / diff) % 6;
    else if (max === g) h = (b - r) / diff + 2;
    else h = (r - g) / diff + 4;
    h = Math.round(h * 60);
    if (h < 0) h += 360;
  }

  return { h, s: max === 0 ? 0 : diff / max, v: max };
}

function hsvToHex(h: number, s: number, v: number): string {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;

  let r = 0,
    g = 0,
    b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }

  const hex2 = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${hex2(r)}${hex2(g)}${hex2(b)}`;
}

function parseHex(hex: string): { h: number; s: number; v: number } | null {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return null;
  return hexToHsv(hex);
}

// ---------------------------------------------------------------------------
// Image upload helpers
// ---------------------------------------------------------------------------

const MAX_IMAGE_BYTES = 8 * 1024 * 1024; // 8 MB
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

interface UploadedImage {
  fileId: string;
  previewUrl: string;
}

function useFileUrl(fileId: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!fileId) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    api
      .getDownloadUrl(fileId)
      .then((r) => {
        if (!cancelled) setUrl(r.downloadUrl);
      })
      .catch(() => {
        /* ignore — preview just won't show */
      });
    return () => {
      cancelled = true;
    };
  }, [fileId]);
  return url;
}

async function uploadImageFile(file: File): Promise<UploadedImage> {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    throw new Error('Please upload a JPEG, PNG, WebP, or GIF image.');
  }
  if (file.size > MAX_IMAGE_BYTES) {
    throw new Error('Image must be smaller than 8 MB.');
  }

  const presigned = await api.getUploadUrl({
    fileName: file.name,
    mimeType: file.type,
    sizeBytes: file.size,
    category: 'media',
  });

  const uploadRes = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!uploadRes.ok) throw new Error('Upload failed. Please try again.');

  const { file: confirmed } = await api.confirmUpload(presigned.fileId, {
    fileName: file.name,
    mimeType: file.type,
    sizeBytes: file.size,
    s3Key: presigned.s3Key,
    category: 'media',
  });

  const { downloadUrl } = await api.getDownloadUrl(confirmed.fileId);
  return { fileId: confirmed.fileId, previewUrl: downloadUrl };
}

interface ImagePickerProps {
  label: string;
  shape?: 'circle' | 'rect';
  currentFileId: string | null;
  previewUrl: string | null;
  fallback?: React.ReactNode;
  onUploaded: (img: UploadedImage) => void;
  onRemove: () => void;
}

function ImagePicker({
  label,
  shape = 'rect',
  currentFileId,
  previewUrl,
  fallback,
  onUploaded,
  onRemove,
}: ImagePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolve URL from saved fileId (for previously-saved images on modal open)
  const savedUrl = useFileUrl(previewUrl ? null : currentFileId);
  const displayUrl = previewUrl ?? savedUrl;

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      setUploading(true);
      try {
        const img = await uploadImageFile(file);
        onUploaded(img);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Upload failed.');
      } finally {
        setUploading(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    },
    [onUploaded],
  );

  const isCircle = shape === 'circle';

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-sm">{label}</Label>
      <div className="flex items-center gap-3">
        {/* Preview */}
        <div
          className={cn(
            'relative flex-shrink-0 bg-muted border border-border overflow-hidden',
            isCircle ? 'w-16 h-16 rounded-full' : 'w-24 h-16 rounded-lg',
          )}
        >
          {displayUrl ? (
            <img src={displayUrl} alt={label} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              {fallback ?? <Upload className="w-5 h-5 text-muted-foreground" />}
            </div>
          )}
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="h-8 text-xs gap-1.5"
          >
            <Upload className="w-3.5 h-3.5" />
            {displayUrl ? 'Replace' : 'Upload'}
          </Button>
          {displayUrl && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRemove}
              className="h-8 text-xs gap-1.5 text-destructive hover:text-destructive"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Remove
            </Button>
          )}
        </div>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES.join(',')}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ColorPicker
// ---------------------------------------------------------------------------

const PICKER_PRESETS = ['#cbbfb5', '#4a3426', '#9b2fae', '#3a7ca5', '#3cab2f', '#5865F2'];

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
}

function ColorPicker({ value, onChange, label }: ColorPickerProps) {
  const [open, setOpen] = useState(false);

  // Internal picker state (HSV)
  const [hue, setHue] = useState(0);
  const [sat, setSat] = useState(1);
  const [bright, setBright] = useState(1);
  const [hexInput, setHexInput] = useState(value.toUpperCase());

  // Refs so drag handlers don't need to be recreated
  const hueRef = useRef(hue);
  const satRef = useRef(sat);
  const brightRef = useRef(bright);
  const onChangeRef = useRef(onChange);
  hueRef.current = hue;
  satRef.current = sat;
  brightRef.current = bright;
  onChangeRef.current = onChange;

  const gradientRef = useRef<HTMLDivElement>(null);
  const hueSliderRef = useRef<HTMLDivElement>(null);
  const isDraggingGradient = useRef(false);
  const isDraggingHue = useRef(false);

  // Sync internal state from prop when the popover opens
  useEffect(() => {
    if (open) {
      const parsed = parseHex(value);
      if (parsed) {
        setHue(parsed.h);
        setSat(parsed.s);
        setBright(parsed.v);
        setHexInput(value.toUpperCase());
      }
    }
  }, [open]);

  // Register document-level drag listeners once
  useEffect(() => {
    function handleMouseMove(e: MouseEvent) {
      if (isDraggingGradient.current && gradientRef.current) {
        const rect = gradientRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
        setSat(x);
        setBright(1 - y);
        const hex = hsvToHex(hueRef.current, x, 1 - y);
        setHexInput(hex.toUpperCase());
        onChangeRef.current(hex);
      }
      if (isDraggingHue.current && hueSliderRef.current) {
        const rect = hueSliderRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const newHue = Math.round(x * 360);
        setHue(newHue);
        const hex = hsvToHex(newHue, satRef.current, brightRef.current);
        setHexInput(hex.toUpperCase());
        onChangeRef.current(hex);
      }
    }

    function handleMouseUp() {
      isDraggingGradient.current = false;
      isDraggingHue.current = false;
    }

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  function handleGradientMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault();
    isDraggingGradient.current = true;
    if (gradientRef.current) {
      const rect = gradientRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
      setSat(x);
      setBright(1 - y);
      const hex = hsvToHex(hueRef.current, x, 1 - y);
      setHexInput(hex.toUpperCase());
      onChange(hex);
    }
  }

  function handleHueMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault();
    isDraggingHue.current = true;
    if (hueSliderRef.current) {
      const rect = hueSliderRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const newHue = Math.round(x * 360);
      setHue(newHue);
      const hex = hsvToHex(newHue, satRef.current, brightRef.current);
      setHexInput(hex.toUpperCase());
      onChange(hex);
    }
  }

  function handleHexChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    setHexInput(raw);
    const parsed = parseHex(raw);
    if (parsed) {
      setHue(parsed.h);
      setSat(parsed.s);
      setBright(parsed.v);
      onChange(raw.toUpperCase());
    }
  }

  function handleHexBlur() {
    if (!parseHex(hexInput)) {
      setHexInput(value.toUpperCase());
    }
  }

  function applyPreset(color: string) {
    const parsed = parseHex(color);
    if (parsed) {
      setHue(parsed.h);
      setSat(parsed.s);
      setBright(parsed.v);
      setHexInput(color.toUpperCase());
      onChange(color);
    }
  }

  const hueColor = hsvToHex(hue, 1, 1);

  return (
    <div className="flex flex-col gap-1.5">
      {label && <Label className="text-sm">{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            title={value}
            className="relative w-12 h-12 rounded-lg border-2 border-border hover:border-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring flex-shrink-0"
            style={{ backgroundColor: value }}
          >
            <Pencil className="absolute bottom-1 right-1 w-3 h-3 text-white drop-shadow-md" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          side="top"
          align="start"
          sideOffset={8}
          className="w-[300px] p-4 bg-[#1c1c1e] border border-white/10 rounded-2xl shadow-2xl"
          onPointerDownOutside={(e) => {
            // Don't close while dragging outside the popover
            if (isDraggingGradient.current || isDraggingHue.current) {
              e.preventDefault();
            }
          }}
        >
          {/* Saturation / Brightness area */}
          <div
            ref={gradientRef}
            onMouseDown={handleGradientMouseDown}
            className="relative w-full h-[200px] rounded-xl cursor-crosshair select-none mb-3 overflow-hidden"
            style={{ backgroundColor: hueColor }}
          >
            {/* White → transparent (left to right) */}
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(to right, #fff, transparent)' }}
            />
            {/* Black → transparent (bottom to top) */}
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(to top, #000, transparent)' }}
            />
            {/* Cursor */}
            <div
              className="absolute w-4 h-4 rounded-full border-2 border-white shadow-md -translate-x-1/2 -translate-y-1/2 pointer-events-none"
              style={{
                left: `${sat * 100}%`,
                top: `${(1 - bright) * 100}%`,
                backgroundColor: hsvToHex(hue, sat, bright),
              }}
            />
          </div>

          {/* Hue slider */}
          <div
            ref={hueSliderRef}
            onMouseDown={handleHueMouseDown}
            className="relative w-full h-3 rounded-full cursor-pointer select-none mb-4"
            style={{
              background:
                'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
            }}
          >
            {/* Thumb */}
            <div
              className="absolute top-1/2 w-4 h-4 rounded-full border-2 border-white shadow-md -translate-x-1/2 -translate-y-1/2 pointer-events-none"
              style={{
                left: `${(hue / 360) * 100}%`,
                backgroundColor: hueColor,
              }}
            />
          </div>

          {/* HEX input */}
          <div className="flex items-center gap-2 mb-3">
            <div
              className="w-8 h-8 rounded-md flex-shrink-0 border border-white/20"
              style={{ backgroundColor: hsvToHex(hue, sat, bright) }}
            />
            <input
              type="text"
              value={hexInput}
              onChange={handleHexChange}
              onBlur={handleHexBlur}
              spellCheck={false}
              className="resize-none w-full text-left rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150 shadow-social-card"
              maxLength={7}
              placeholder="#000000"
            />
          </div>

          {/* Preset swatches */}
          <div className="flex gap-2">
            {PICKER_PRESETS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => applyPreset(c)}
                title={c}
                className={cn(
                  'w-9 h-9 rounded-lg border-2 transition-transform hover:scale-110 focus:outline-none',
                  value.toLowerCase() === c.toLowerCase()
                    ? 'border-white scale-110'
                    : 'border-transparent',
                )}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProfileCard
// ---------------------------------------------------------------------------

interface ProfileCardProps {
  displayName: string;
  username: string;
  bio: string;
  bannerColor: string;
  avatarColor: string;
  avatarImageUrl?: string | null;
}

function ProfileCard({
  displayName,
  username,
  bio,
  bannerColor,
  avatarColor,
  avatarImageUrl,
}: ProfileCardProps) {
  const name = displayName.trim() || username;
  const handle = `@${username}`;
  const initial = name.charAt(0).toUpperCase();

  return (
    <div className="relative rounded-[18px] bg-[#1c1c1e] border border-white/10 overflow-hidden max-w-[360px] shadow-xl">
      {/* Banner */}
      <div
        className="h-[100px] w-full transition-colors duration-300"
        style={{ backgroundColor: bannerColor }}
      />

      {/* Avatar — overlaps banner */}
      <div className="relative px-4 pb-4">
        <div
          className="absolute -top-9 left-4 w-[72px] h-[72px] rounded-full border-4 border-[#1c1c1e] overflow-hidden flex items-center justify-center text-white text-2xl font-bold select-none transition-colors duration-300"
          style={avatarImageUrl ? undefined : { backgroundColor: avatarColor }}
        >
          {avatarImageUrl ? (
            <img src={avatarImageUrl} alt={name} className="w-full h-full object-cover" />
          ) : (
            initial
          )}
        </div>

        {/* Text content */}
        <div className="pt-11">
          <p className="text-[20px] font-bold text-white leading-tight">{name}</p>
          <p className="text-sm text-[#a1a1aa] mt-0.5">{handle}</p>
          {bio && <p className="text-sm text-[#d4d4d8] mt-2 leading-relaxed break-words">{bio}</p>}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared section primitives
// ---------------------------------------------------------------------------

function SettingRow({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-border/50 last:border-0">
      <div className="flex flex-col gap-0.5 mr-4">
        <span className="text-sm font-medium">{label}</span>
        {description && <span className="text-xs text-muted-foreground">{description}</span>}
      </div>
      {children}
    </div>
  );
}

function SectionHeading({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mb-6">
      <h2 className="text-base font-semibold">{title}</h2>
      {description && <p className="text-sm text-muted-foreground mt-0.5">{description}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Profile
// ---------------------------------------------------------------------------

function ProfileSection({
  profile,
  username,
  onChange,
}: {
  profile: ProfileSettings;
  username: string;
  onChange: (p: ProfileSettings) => void;
}) {
  // Resolve the download URL for a previously-saved avatar so the preview card shows it
  const savedAvatarUrl = useFileUrl(profile.avatarPreviewUrl ? null : profile.avatarImageId);
  const avatarImageUrl = profile.avatarPreviewUrl ?? savedAvatarUrl;

  return (
    <div className="space-y-6">
      <SectionHeading title="Profile" description="Customize how you appear in chat." />

      {/* Live preview */}
      <ProfileCard
        displayName={profile.displayName}
        username={username}
        bio={profile.bio}
        bannerColor={profile.bannerColor}
        avatarColor={profile.avatarColor}
        avatarImageUrl={avatarImageUrl}
      />

      {/* Avatar image */}
      <ImagePicker
        label="Avatar Photo"
        shape="circle"
        currentFileId={profile.avatarImageId}
        previewUrl={profile.avatarPreviewUrl}
        fallback={
          <div
            className="w-full h-full flex items-center justify-center text-white text-xl font-bold"
            style={{ backgroundColor: profile.avatarColor }}
          >
            {(profile.displayName.trim() || username).charAt(0).toUpperCase()}
          </div>
        }
        onUploaded={(img) =>
          onChange({ ...profile, avatarImageId: img.fileId, avatarPreviewUrl: img.previewUrl })
        }
        onRemove={() => onChange({ ...profile, avatarImageId: null, avatarPreviewUrl: null })}
      />

      {/* Display name */}
      <div className="space-y-1.5">
        <Label htmlFor="displayName">Display Name</Label>
        <Input
          id="displayName"
          value={profile.displayName}
          onChange={(e) => onChange({ ...profile, displayName: e.target.value })}
          placeholder={username}
          maxLength={40}
          className="resize-none w-full text-left rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none leading-relaxed rounded-[10px] outline-none transition-all duration-150 shadow-social-card"
        />
        <p className="text-xs text-muted-foreground">Leave blank to use your account username.</p>
      </div>

      {/* Bio */}
      <div className="space-y-1.5">
        <Label htmlFor="bio">Bio</Label>
        <Textarea
          id="bio"
          value={profile.bio}
          onChange={(e) => onChange({ ...profile, bio: e.target.value })}
          placeholder="Tell people a bit about yourself..."
          maxLength={190}
          rows={3}
          className="resize-none w-full text-left rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none leading-relaxed rounded-[10px] outline-none transition-all duration-150 shadow-social-card"
        />
        <p className="text-xs text-muted-foreground text-right">{profile.bio.length}/190</p>
      </div>

      {/* Color pickers */}
      <div className="flex gap-6">
        <ColorPicker
          label="Banner Color"
          value={profile.bannerColor}
          onChange={(c) => onChange({ ...profile, bannerColor: c })}
        />
        <ColorPicker
          label="Avatar Color"
          value={profile.avatarColor}
          onChange={(c) => onChange({ ...profile, avatarColor: c })}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Notifications
// ---------------------------------------------------------------------------

function NotificationsSection({
  notifications,
  onChange,
}: {
  notifications: NotificationSettings;
  onChange: (n: NotificationSettings) => void;
}) {
  return (
    <div className="space-y-6">
      <SectionHeading title="Notifications" description="Control when and how you're notified." />

      <div className="space-y-2">
        <Label className="text-sm font-medium">New Message Alerts</Label>
        <div className="flex flex-col gap-1 mt-2">
          {(
            [
              { value: 'all', label: 'All Messages', desc: 'Notify for every new message' },
              { value: 'dms', label: 'Direct Messages Only', desc: 'Only DMs and mentions' },
              { value: 'never', label: 'Never', desc: 'No message notifications' },
            ] as const
          ).map(({ value, label, desc }) => (
            <button
              key={value}
              type="button"
              onClick={() => onChange({ ...notifications, newMessages: value })}
              className={cn(
                'flex items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                notifications.newMessages === value
                  ? 'bg-primary/10 border border-primary/30'
                  : 'border border-transparent hover:bg-muted',
              )}
            >
              <span
                className={cn(
                  'mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center',
                  notifications.newMessages === value
                    ? 'border-primary bg-primary'
                    : 'border-muted-foreground',
                )}
              >
                {notifications.newMessages === value && (
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                )}
              </span>
              <div>
                <p className="text-sm font-medium">{label}</p>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-0 divide-y divide-border/50">
        <SettingRow
          label="Reaction Notifications"
          description="When someone reacts to your message"
        >
          <Switch
            checked={notifications.reactions}
            onCheckedChange={(v) => onChange({ ...notifications, reactions: v })}
          />
        </SettingRow>
        <SettingRow
          label="Added to Channel"
          description="When you're added to a new group or channel"
        >
          <Switch
            checked={notifications.channelAdded}
            onCheckedChange={(v) => onChange({ ...notifications, channelAdded: v })}
          />
        </SettingRow>
        <SettingRow label="DM Sounds" description="Play a sound for new direct messages">
          <Switch
            checked={notifications.soundDMs}
            onCheckedChange={(v) => onChange({ ...notifications, soundDMs: v })}
          />
        </SettingRow>
        <SettingRow label="Channel Sounds" description="Play a sound for new channel messages">
          <Switch
            checked={notifications.soundChannels}
            onCheckedChange={(v) => onChange({ ...notifications, soundChannels: v })}
          />
        </SettingRow>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Appearance
// ---------------------------------------------------------------------------

function AppearanceSection({
  appearance,
  onChange,
}: {
  appearance: AppearanceSettings;
  onChange: (a: AppearanceSettings) => void;
}) {
  return (
    <div className="space-y-6">
      <SectionHeading title="Appearance" description="Customize the look and feel of your chat." />

      <ColorPicker
        label="Accent Color"
        value={appearance.accentColor}
        onChange={(c) => onChange({ ...appearance, accentColor: c })}
      />
      <p className="text-xs text-muted-foreground -mt-4">Used for highlights and active states.</p>

      {/* Channel List Layout */}
      <div className="space-y-3">
        <div>
          <p className="text-sm font-medium">Channel List Layout</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Community is always pinned at the top.
          </p>
        </div>
        <div className="flex flex-col gap-1">
          {(
            [
              {
                value: 'sections',
                label: 'Sections',
                desc: 'Community, Guilds, Groups, and Direct Messages shown separately',
              },
              {
                value: 'unified',
                label: 'Unified List',
                desc: 'All channels in one list with Community at the top',
              },
            ] as const
          ).map(({ value, label, desc }) => (
            <button
              key={value}
              type="button"
              onClick={() => onChange({ ...appearance, channelListView: value })}
              className={cn(
                'flex items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                appearance.channelListView === value
                  ? 'bg-primary/10 border border-primary/30'
                  : 'border border-transparent hover:bg-muted',
              )}
            >
              <span
                className={cn(
                  'mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center',
                  appearance.channelListView === value
                    ? 'border-primary bg-primary'
                    : 'border-muted-foreground',
                )}
              >
                {appearance.channelListView === value && (
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                )}
              </span>
              <div>
                <p className="text-sm font-medium">{label}</p>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Chat Background */}
      <div className="space-y-3">
        <div>
          <p className="text-sm font-medium">Chat Background</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Shown behind messages in all your chats.
          </p>
        </div>

        <ImagePicker
          label="Background Image"
          shape="rect"
          currentFileId={appearance.backgroundImageId}
          previewUrl={appearance.backgroundPreviewUrl}
          onUploaded={(img) =>
            onChange({
              ...appearance,
              backgroundImageId: img.fileId,
              backgroundPreviewUrl: img.previewUrl,
              backgroundColor: '',
            })
          }
          onRemove={() =>
            onChange({
              ...appearance,
              backgroundImageId: null,
              backgroundPreviewUrl: null,
            })
          }
        />

        {appearance.backgroundImageId ? (
          <SettingRow label="Blur Background" description="Apply a frosted-glass blur to the image">
            <Switch
              checked={appearance.backgroundBlur}
              onCheckedChange={(v) => onChange({ ...appearance, backgroundBlur: v })}
            />
          </SettingRow>
        ) : (
          <div className="flex items-end gap-4">
            <ColorPicker
              label="Background Color"
              value={appearance.backgroundColor || '#ffffff'}
              onChange={(c) => onChange({ ...appearance, backgroundColor: c })}
            />
            {appearance.backgroundColor && (
              <button
                type="button"
                onClick={() => onChange({ ...appearance, backgroundColor: '' })}
                className="text-xs text-muted-foreground hover:text-foreground mb-1.5 transition-colors"
              >
                Reset to default
              </button>
            )}
          </div>
        )}
      </div>

      {/* <div className="space-y-0 divide-y divide-border/50">
        <SettingRow label="Show Emoji" description="Render emoji in messages">
          <Switch
            checked={appearance.showEmojis}
            onCheckedChange={(v) => onChange({ ...appearance, showEmojis: v })}
          />
        </SettingRow>
        <SettingRow label="Show Reactions" description="Display emoji reactions on messages">
          <Switch
            checked={appearance.showReactions}
            onCheckedChange={(v) => onChange({ ...appearance, showReactions: v })}
          />
        </SettingRow>
        <SettingRow label="Compact Mode" description="Reduce spacing between messages">
          <Switch
            checked={appearance.compactMode}
            onCheckedChange={(v) => onChange({ ...appearance, compactMode: v })}
          />
        </SettingRow>
      </div> */}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Privacy
// ---------------------------------------------------------------------------

function PrivacySection({
  privacy,
  onChange,
}: {
  privacy: PrivacySettings;
  onChange: (p: PrivacySettings) => void;
}) {
  return (
    <div className="space-y-6">
      <SectionHeading
        title="Privacy"
        description="Control who can contact you and what they can see."
      />

      <div className="space-y-2">
        <Label className="text-sm font-medium">Who Can Send You Direct Messages</Label>
        <div className="flex flex-col gap-1 mt-2">
          {(
            [
              { value: 'everyone', label: 'Everyone', desc: 'Any Fias member can DM you' },
              { value: 'nobody', label: 'Nobody', desc: 'No one can start a new DM with you' },
            ] as const
          ).map(({ value, label, desc }) => (
            <button
              key={value}
              type="button"
              onClick={() => onChange({ ...privacy, whoCanDM: value })}
              className={cn(
                'flex items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                privacy.whoCanDM === value
                  ? 'bg-primary/10 border border-primary/30'
                  : 'border border-transparent hover:bg-muted',
              )}
            >
              <span
                className={cn(
                  'mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center',
                  privacy.whoCanDM === value
                    ? 'border-primary bg-primary'
                    : 'border-muted-foreground',
                )}
              >
                {privacy.whoCanDM === value && (
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                )}
              </span>
              <div>
                <p className="text-sm font-medium">{label}</p>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-0 divide-y divide-border/50">
        <SettingRow
          label="Read Receipts"
          description="Let others see when you've read their messages"
        >
          <Switch
            checked={privacy.readReceipts}
            onCheckedChange={(v) => onChange({ ...privacy, readReceipts: v })}
          />
        </SettingRow>
        <SettingRow label="Show Online Status" description="Let others see when you're active">
          <Switch
            checked={privacy.showOnlineStatus}
            onCheckedChange={(v) => onChange({ ...privacy, showOnlineStatus: v })}
          />
        </SettingRow>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab nav item
// ---------------------------------------------------------------------------

type TabName = 'Profile' | 'Notifications' | 'Appearance' | 'Privacy';

interface TabDef {
  icon: React.ReactNode;
  name: TabName;
}

const TABS: TabDef[] = [
  { icon: <UserCircle className="w-4 h-4" />, name: 'Profile' },
  { icon: <Bell className="w-4 h-4" />, name: 'Notifications' },
  { icon: <Palette className="w-4 h-4" />, name: 'Appearance' },
  { icon: <Shield className="w-4 h-4" />, name: 'Privacy' },
];

function TabItem({
  tab,
  active,
  onClick,
  mobile = false,
}: {
  tab: TabDef;
  active: boolean;
  onClick: () => void;
  mobile?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 transition-colors rounded-lg text-sm',
        mobile ? 'px-3 py-2 whitespace-nowrap flex-shrink-0' : 'px-3 py-2 w-full text-left',
        active
          ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      <span className="flex-shrink-0">{tab.icon}</span>
      <span>{tab.name}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

interface ChatSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function serverSettingsToLocal(s: ReturnType<typeof useChatSettings>['settings']): ChatSettings {
  return {
    profile: {
      displayName: s.displayName,
      bio: s.bio,
      bannerColor: s.bannerColor,
      avatarColor: s.avatarColor,
      avatarImageId: s.avatarImageId,
      // previewUrl is resolved lazily in ImagePicker via useFileUrl
      avatarPreviewUrl: null,
    },
    notifications: {
      newMessages: s.notificationNewMessages,
      reactions: s.notificationReactions,
      channelAdded: s.notificationChannelAdded,
      soundDMs: s.notificationSoundDMs,
      soundChannels: s.notificationSoundChannels,
    },
    appearance: {
      showEmojis: s.appearanceShowEmojis,
      showReactions: s.appearanceShowReactions,
      compactMode: s.appearanceCompactMode,
      accentColor: s.appearanceAccentColor,
      channelListView: s.appearanceChannelListView,
      backgroundImageId: s.backgroundImageId,
      backgroundPreviewUrl: null,
      backgroundBlur: s.backgroundBlur,
      backgroundColor: s.backgroundColor,
    },
    privacy: {
      whoCanDM: s.privacyWhoCanDM,
      readReceipts: s.privacyReadReceipts,
      showOnlineStatus: s.privacyShowOnlineStatus,
    },
  };
}

export function ChatSettingsModal({ open, onOpenChange }: ChatSettingsModalProps) {
  const { user } = useAuth();
  const username = user?.username ?? '';

  const { settings: serverSettings, saveSettings } = useChatSettings();

  const [activeTab, setActiveTab] = useState<TabName>('Profile');
  const [savedSettings, setSavedSettings] = useState<ChatSettings>(DEFAULT_SETTINGS);
  const [settings, setSettings] = useState<ChatSettings>(DEFAULT_SETTINGS);

  // Sync local state when server settings load or change
  useEffect(() => {
    const local = serverSettingsToLocal(serverSettings);
    setSavedSettings(local);
    setSettings(local);
  }, [serverSettings]);

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(savedSettings);

  function update(next: ChatSettings) {
    setSettings(next);
  }

  async function handleSave() {
    await saveSettings({
      displayName: settings.profile.displayName,
      bio: settings.profile.bio,
      bannerColor: settings.profile.bannerColor,
      avatarColor: settings.profile.avatarColor,
      avatarImageId: settings.profile.avatarImageId,
      notificationNewMessages: settings.notifications.newMessages,
      notificationReactions: settings.notifications.reactions,
      notificationChannelAdded: settings.notifications.channelAdded,
      notificationSoundDMs: settings.notifications.soundDMs,
      notificationSoundChannels: settings.notifications.soundChannels,
      appearanceShowEmojis: settings.appearance.showEmojis,
      appearanceShowReactions: settings.appearance.showReactions,
      appearanceCompactMode: settings.appearance.compactMode,
      appearanceAccentColor: settings.appearance.accentColor,
      appearanceChannelListView: settings.appearance.channelListView,
      backgroundImageId: settings.appearance.backgroundImageId,
      backgroundBlur: settings.appearance.backgroundBlur,
      backgroundColor: settings.appearance.backgroundColor,
      privacyWhoCanDM: settings.privacy.whoCanDM,
      privacyReadReceipts: settings.privacy.readReceipts,
      privacyShowOnlineStatus: settings.privacy.showOnlineStatus,
    });
    setSavedSettings(settings);
  }

  function handleReset() {
    setSettings(savedSettings);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="p-0 gap-0 flex flex-col overflow-hidden w-full max-w-full h-screen rounded-none translate-x-0 translate-y-0 top-0 left-0 border-0 sm:max-w-2xl sm:h-[85vh] sm:max-h-[720px] sm:rounded-xl sm:border sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%]"
      >
        <DialogTitle className="sr-only">Chat Settings</DialogTitle>
        <DialogDescription className="sr-only">
          Manage your chat profile, notifications, appearance, and privacy.
        </DialogDescription>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <span className="text-base font-semibold">Settings</span>
          <DialogClose asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
              <X className="h-4 w-4" />
            </Button>
          </DialogClose>
        </div>

        {/* Mobile: horizontal tab bar */}
        <div className="md:hidden flex overflow-x-auto gap-1 px-3 py-2 border-b border-border flex-shrink-0 scrollbar-none">
          {TABS.map((tab) => (
            <TabItem
              key={tab.name}
              tab={tab}
              active={activeTab === tab.name}
              onClick={() => setActiveTab(tab.name)}
              mobile
            />
          ))}
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden relative">
          {/* Desktop sidebar */}
          <aside className="hidden md:flex flex-col w-48 flex-shrink-0 border-r border-border p-3 gap-0.5 overflow-y-auto">
            {TABS.map((tab) => (
              <TabItem
                key={tab.name}
                tab={tab}
                active={activeTab === tab.name}
                onClick={() => setActiveTab(tab.name)}
              />
            ))}
          </aside>

          {/* Content panel */}
          <div className="flex-1 overflow-y-auto px-5 py-5 pb-20">
            {activeTab === 'Profile' && (
              <ProfileSection
                profile={settings.profile}
                username={username}
                onChange={(profile) => update({ ...settings, profile })}
              />
            )}
            {activeTab === 'Notifications' && (
              <NotificationsSection
                notifications={settings.notifications}
                onChange={(notifications) => update({ ...settings, notifications })}
              />
            )}
            {activeTab === 'Appearance' && (
              <AppearanceSection
                appearance={settings.appearance}
                onChange={(appearance) => update({ ...settings, appearance })}
              />
            )}
            {activeTab === 'Privacy' && (
              <PrivacySection
                privacy={settings.privacy}
                onChange={(privacy) => update({ ...settings, privacy })}
              />
            )}
          </div>

          {/* Floating unsaved-changes footer */}
          {hasChanges && (
            <div className="absolute bottom-0 inset-x-0 flex items-center justify-between gap-4 px-5 py-3 bg-background/95 backdrop-blur-sm border-t border-border shadow-[0_-4px_16px_rgba(0,0,0,0.08)]">
              <p className="text-sm text-muted-foreground">
                Careful — your changes have not yet been saved.
              </p>
              <div className="flex items-center gap-4 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleReset}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Reset
                </button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  className="cursor-pointer bg-foreground p-1 px-2 text-[10px] text-background rounded-md h-fit hover:bg-foreground/80 flex items-center gap-2"
                >
                  <Save className="h-3.5 w-3.5" />
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
